import React, { useState, useEffect } from 'react';
import { db, storage } from '../../config/firebase';
import { collection, addDoc, doc, setDoc, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAppContext } from '../../context/AppContext';
import { sendEmailNotification } from '../../utils/emailService';
import { getStage1Price } from '../../config/paymentLinks';
import PricingTableModal from '../PricingTableModal';

const STAGE_3_4_ENABLED = false;

const ProjectModal = () => {
  const {
    showProjectModal,
    setShowProjectModal,
    currentUser,
    developmentSettings,
    pricing,
    typeNames,
    calculateStage1PaymentStructure,
    calculateStage2PaymentStructure,
    setProjects,
    showNotification,
    setShowPaymentModal,
    setPaymentProject,
    showSampleViewer,
    setShowSampleViewer,
    showFeasibilityViewer, 
    setShowFeasibilityViewer 
  } = useAppContext();

  const [projectData, setProjectData] = useState({
    name: '',
    address: '',
    lotSize: '',
    type: 'single',
    stage: 1,
    description: '',
    brief: '',
    storeys: 1,
    townhouseUnits: '3-5',
    majorRenovationStoreys: 1,
    supportingDocuments: [],
    previousStageCompleted: 'no',
    previousProjectRef: ''
  });

  const [hasCompletedStage1, setHasCompletedStage1] = useState(null);
  const [checkingStage1, setCheckingStage1] = useState(false);
  const [showPricingTable, setShowPricingTable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check for Stage 1 completion when address changes and stage is 2
  useEffect(() => {
    const checkStage1Completion = async () => {
      if (!currentUser || !currentUser.email) {
        setHasCompletedStage1(null);
        setCheckingStage1(false);
        return;
      }
      
      if (projectData.stage === 2 && projectData.address && projectData.address.length > 5) {
        setCheckingStage1(true);
        try {
          const normalizedAddress = projectData.address.toLowerCase().trim();
          
          const q = query(collection(db, 'projects'), 
            where('stage', '==', 1),
            where('userEmail', '==', currentUser.email)
          );
          
          const querySnapshot = await getDocs(q);
          let foundStage1 = false;
          
          querySnapshot.forEach((doc) => {
            const project = doc.data();
            const projectAddress = project.address?.toLowerCase().trim() || '';
            
            if (projectAddress && normalizedAddress.includes(projectAddress.substring(0, 20)) || 
                projectAddress.includes(normalizedAddress.substring(0, 20))) {
              if (project.status === 'stage-1-approved' || 
                  project.status === 'stage-1-completed' || 
                  project.status === 'paid' ||
                  project.paymentStatus === 'paid') {
                foundStage1 = true;
              }
            }
          });
          
          setHasCompletedStage1(foundStage1);
        } catch (error) {
          console.error('Error checking Stage 1 completion:', error);
        }
        setCheckingStage1(false);
      } else {
        setHasCompletedStage1(null);
        setCheckingStage1(false);
      }
    };

    checkStage1Completion();
  }, [projectData.address, projectData.stage, currentUser]);

  // Reset submitting state when modal opens
  useEffect(() => {
    if (showProjectModal) {
      setIsSubmitting(false);
    }
  }, [showProjectModal]);

  if (!showProjectModal) {
    return null;
  }

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    const uploadedFiles = files.map(file => ({
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString()
    }));
    
    setProjectData(prev => ({
      ...prev,
      supportingDocuments: [...prev.supportingDocuments, ...uploadedFiles]
    }));
    
    showNotification("Files Added", `${files.length} file(s) added successfully`, "success");
  };

  const removeFile = (index) => {
    setProjectData(prev => ({
      ...prev,
      supportingDocuments: prev.supportingDocuments.filter((_, i) => i !== index)
    }));
  };

  const uploadFilesToStorage = async (projectId, files) => {
    const uploadedDocs = [];
    
    for (const fileData of files) {
      if (fileData.file) {
        try {
          const timestamp = Date.now();
          const safeName = fileData.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const storageRef = ref(storage, `projects/${projectId}/supporting-documents/${timestamp}-${safeName}`);
          
          console.log('Uploading file:', fileData.name);
          const snapshot = await uploadBytes(storageRef, fileData.file);
          
          const downloadURL = await getDownloadURL(snapshot.ref);
          
          uploadedDocs.push({
            name: fileData.name,
            size: fileData.size,
            type: fileData.type,
            uploadDate: fileData.uploadDate,
            url: downloadURL,
            storagePath: snapshot.ref.fullPath
          });
          
          console.log('File uploaded successfully:', fileData.name, 'URL:', downloadURL);
        } catch (error) {
          console.error('Error uploading file:', fileData.name, error);
          uploadedDocs.push({
            name: fileData.name,
            size: fileData.size,
            type: fileData.type,
            uploadDate: fileData.uploadDate,
            url: null,
            error: 'Upload failed: ' + error.message
          });
        }
      } else {
        uploadedDocs.push({
          name: fileData.name,
          size: fileData.size,
          type: fileData.type,
          uploadDate: fileData.uploadDate,
          url: null
        });
      }
    }
    
    return uploadedDocs;
  };

  const createProject = async (projectData) => {
    if (!currentUser || !currentUser.email) {
      showNotification('Error', 'You must be logged in to create a project', 'error');
      return null;
    }
    
    const settings = developmentSettings[projectData.type];
    
    // Calculate cost and structure for different stages
    let cost = pricing[projectData.type]?.[projectData.stage] || 'Quote Required';
    let paymentStructure = null;
    let initialPaymentMessage = '';
    let additionalRevisionFee = 150;
    let conceptTimeline = 5;
    let revisionTimeline = 3;
    let standardCost = null;
    let includesSiteInvestigation = false;
    
    if (projectData.stage === 1) {
      // NEW STAGE 1 MODEL: Single payment on delivery (or auto-charge after 5 days)
      const stage1Price = getStage1Price(projectData.type, projectData.townhouseUnits) || 199;
      cost = '$' + stage1Price;
      
      // Single payment structure
      paymentStructure = {
        totalPayments: 1,
        paymentAmounts: [stage1Price],
        paymentLabels: ['Full Payment'],
        paymentModel: 'card-capture-auto-charge'
      };
      
      initialPaymentMessage = `Your Stage 1 project has been created. Please provide your card details to begin work. Your card will NOT be charged now — it will only be charged if payment is not received within 5 days of invoice delivery.`;
      
    } else if (projectData.stage === 2) {
      // Stage 2 cost calculation logic (existing code)
      let baseStage2Cost = 0;
      
      if (projectData.type === 'single' || projectData.type === 'duplex') {
        if (projectData.storeys === 1) {
          baseStage2Cost = 399;
          additionalRevisionFee = 99;
          conceptTimeline = 4;
          revisionTimeline = 2;
        } else if (projectData.storeys === 2) {
          baseStage2Cost = 550;
          additionalRevisionFee = 150;
          conceptTimeline = 5;
          revisionTimeline = 3;
        } else if (projectData.storeys === 3) {
          baseStage2Cost = 650;
          additionalRevisionFee = 150;
          conceptTimeline = 6;
          revisionTimeline = 3;
        }
      } else if (projectData.type === 'townhouse') {
        if (projectData.townhouseUnits === '3-5') {
          baseStage2Cost = 799;
          additionalRevisionFee = 150;
          conceptTimeline = 6;
          revisionTimeline = 3;
        } else if (projectData.townhouseUnits === '6-9') {
          baseStage2Cost = 1099;
          additionalRevisionFee = 150;
          conceptTimeline = 8;
          revisionTimeline = 3;
        }
      } else if (projectData.type === 'secondary' || projectData.type === 'minor-renovation') {
        baseStage2Cost = 299;
        additionalRevisionFee = 99;
        conceptTimeline = 4;
        revisionTimeline = 2;
      } else if (projectData.type === 'major-renovation') {
        baseStage2Cost = 349;
        additionalRevisionFee = 99;
        conceptTimeline = 5;
        revisionTimeline = 2;
      }
      
      if (hasCompletedStage1 === false) {
        const stage1Price = getStage1Price(projectData.type, projectData.townhouseUnits) || 199;
        cost = '$' + (baseStage2Cost + stage1Price);
        standardCost = '$' + baseStage2Cost;
        includesSiteInvestigation = true;
      } else {
        cost = '$' + baseStage2Cost;
        standardCost = '$' + baseStage2Cost;
      }
      
      paymentStructure = calculateStage2PaymentStructure(cost);
      initialPaymentMessage = `Your Stage 2 project has been submitted. Initial payment of $${paymentStructure.paymentAmounts[0]} is required before concept design work can begin. Total cost: ${cost} (${paymentStructure.totalPayments} payments).`;
      
      if (includesSiteInvestigation) {
        initialPaymentMessage += ' Note: This project includes integrated desktop site investigation as Stage 1 was not completed separately.';
      }
    } else if (projectData.stage === 3 || projectData.stage === 4) {
      // Stage 3 & 4 handling
      cost = 'Quote Required';
      paymentStructure = null;
      initialPaymentMessage = `Your Stage ${projectData.stage} project has been submitted. planAIdesign will assess your requirements and provide a custom quote within 3-5 business days. If a site inspection is required, you will receive a message to arrange site access.`;
    }
    
    const localId = Date.now();
    
    const newProject = {
      id: localId,
      ...projectData,
      storeys: (projectData.type === 'single' || projectData.type === 'duplex') ? projectData.storeys : 
                projectData.type === 'major-renovation' ? projectData.majorRenovationStoreys : 1,
      client: currentUser.name,
      email: currentUser.email,
      userEmail: currentUser.email,
      // NEW STATUS FOR STAGE 1: awaiting-card-capture
      status: projectData.stage >= 3 ? "quote-in-progress" : 
              (projectData.stage === 1 ? "awaiting-card-capture" : "awaiting-payment"),
      // NEW PAYMENT STATUS FOR STAGE 1: pending (card not yet captured)
      paymentStatus: projectData.stage >= 3 ? "pending-quote" : "pending",
      createdDate: new Date().toISOString(),
      cost: cost,
      standardCost: standardCost,
      canRequestComplexityQuote: projectData.stage === 2,
      hasCompletedStage1: hasCompletedStage1,
      includesSiteInvestigation: includesSiteInvestigation,
      paymentStructure: paymentStructure,
      paymentsMade: 0,
      paymentHistory: [],
      communications: [
        {
          type: "system",
          sender: "planAIdesign System",
          message: initialPaymentMessage,
          timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
        }
      ],
      deliverables: [],
      reviews: [],
      revisions: projectData.stage === 2 ? {
        currentRound: 0,
        maxIncludedRevisions: 1,
        totalRevisionsUsed: 0,
        additionalRevisionFee: additionalRevisionFee,
        conceptTimeline: conceptTimeline,
        revisionTimeline: revisionTimeline,
        versions: []
      } : null,
      ...(projectData.stage === 3 && {
        documentRevisionTracking: {},
        applicationStatus: null,
        authorityFees: null
      }),
      ...(projectData.stage === 4 && {
        documentRevisionTracking: {},
        constructionPackageStatus: null
      })
    };
    
    // Save to Firebase
    let docRef;
    try {
      const projectForFirebase = { ...newProject };
      delete projectForFirebase.id;
      delete projectForFirebase.supportingDocuments;
      
      docRef = await addDoc(collection(db, 'projects'), {
        ...projectForFirebase,
        userId: currentUser.id || currentUser.email,
        createdAt: new Date().toISOString(),
        localId: newProject.id
      });
      
      newProject.id = docRef.id;
      newProject.firebaseId = docRef.id;
      newProject.localId = localId;
      
      console.log('Project created with Firebase ID:', docRef.id);

      // Meta Pixel: project created (Lead conversion)
      if (window.fbq) window.fbq('track', 'Lead');

      // Upload supporting documents if any
      let uploadedDocuments = [];
      if (projectData.supportingDocuments && projectData.supportingDocuments.length > 0) {
        console.log('Uploading supporting documents...');
        try {
          uploadedDocuments = await uploadFilesToStorage(docRef.id, projectData.supportingDocuments);
          console.log('Documents uploaded:', uploadedDocuments);
          
          await setDoc(doc(db, 'projects', docRef.id), {
            supportingDocuments: uploadedDocuments,
            firebaseId: docRef.id
          }, { merge: true });
          
          newProject.supportingDocuments = uploadedDocuments;
        } catch (uploadError) {
          console.error('Error uploading documents:', uploadError);
          showNotification('Warning', 'Project created but some files failed to upload', 'warning');
        }
      } else {
        await setDoc(doc(db, 'projects', docRef.id), {
          supportingDocuments: [],
          firebaseId: docRef.id
        }, { merge: true });
      }
      
    } catch (error) {
      console.error('Error creating project:', error);
      showNotification('Error', 'Failed to save project', 'error');
      return null;
    }
    
    // Send email notification about new project
    try {
      const timestamp = new Date().toLocaleString('en-AU', {
        timeZone: 'Australia/Sydney',
        dateStyle: 'medium',
        timeStyle: 'short'
      });
      
      const stageNames = {
        1: 'Desktop Site Investigation & Feasibility',
        2: 'Concept Design',
        3: 'Authority Approval Documentation',
        4: 'Construction Documentation'
      };
      
      let projectDetails = `
        <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Project Name:</strong> ${projectData.name}</p>
        <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Client:</strong> ${currentUser.name} (${currentUser.email})</p>
        <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Address:</strong> ${projectData.address}</p>
        <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Type:</strong> ${typeNames[projectData.type]}</p>
        <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Stage:</strong> ${projectData.stage} - ${stageNames[projectData.stage]}</p>
      `;
      
      if ((projectData.type === 'single' || projectData.type === 'duplex') && projectData.stage === 2) {
        projectDetails += `<p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Storeys:</strong> ${projectData.storeys}</p>`;
      }
      if (projectData.type === 'townhouse' && projectData.stage === 2) {
        projectDetails += `<p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Units:</strong> ${projectData.townhouseUnits}</p>`;
      }
      
      projectDetails += `
        <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Cost:</strong> ${cost}</p>
        <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Lot Size:</strong> ${projectData.lotSize || 'Not specified'} sqm</p>
        <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Created:</strong> ${timestamp}</p>
      `;
      
      if (projectData.stage >= 3) {
        projectDetails += `
          <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Previous Stage Completed:</strong> ${projectData.previousStageCompleted}</p>
          ${projectData.previousProjectRef ? `<p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Previous Project Reference:</strong> ${projectData.previousProjectRef}</p>` : ''}
        `;
      }
      
      if (includesSiteInvestigation) {
        projectDetails += `
          <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Note:</strong> Includes integrated desktop site investigation</p>
        `;
      }
      
      if (newProject.supportingDocuments && newProject.supportingDocuments.length > 0) {
        projectDetails += `
          <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Supporting Documents:</strong> ${newProject.supportingDocuments.length} file(s) uploaded</p>
        `;
      }
      
      // Stage 1 specific messaging
      if (projectData.stage === 1) {
        projectDetails += `
          <div style="background-color: #D1FAE5; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p style="color: #065F46; margin: 0; font-weight: bold;">Card Capture Required</p>
            <p style="color: #065F46; margin: 5px 0;">Client needs to provide card details to begin work</p>
            <p style="color: #065F46; margin: 5px 0;">Payment model: ${cost} charged on delivery (or auto-charge after 5 days)</p>
          </div>
        `;
      } else if (projectData.stage >= 3) {
        projectDetails += `
          <div style="background-color: #FEF3C7; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p style="color: #92400E; margin: 0; font-weight: bold;">Custom Quote Required</p>
            <p style="color: #92400E; margin: 5px 0;">planAIdesign will assess requirements and provide quote within 2-3 business days</p>
          </div>
        `;
      } else {
        projectDetails += `
          <div style="background-color: #FEF3C7; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p style="color: #92400E; margin: 0; font-weight: bold;">Payment Required</p>
            <p style="color: #92400E; margin: 5px 0;">Initial payment of $${paymentStructure.paymentAmounts[0]} required to begin work</p>
          </div>
        `;
      }
      
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; padding: 20px; background-color: #f5f5f5;">
          <div style="max-width: 600px; margin: 0 auto; background-color: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
            <h2 style="color: #333; border-bottom: 2px solid #4F46E5; padding-bottom: 10px;">New Project Created</h2>
            
            <div style="margin: 20px 0;">
              ${projectDetails}
            </div>
            
            <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #eee; text-align: center;">
              <p style="color: #666; font-size: 12px;">planAIdesign - Professional design by experienced building designers</p>
            </div>
          </div>
        </div>
      `;
      
      await sendEmailNotification(
        'admin@planaidesign.com.au',
        `New ${stageNames[projectData.stage]} Project: ${projectData.name}`,
        emailHtml
      );
      
      console.log('Email notification sent');
    } catch (emailError) {
      console.error('Failed to send email notification:', emailError);
    }
    
    // Update local projects state
    setProjects(prev => [newProject, ...prev]);
    
    // Return the new project - UI updates will happen in handleSubmit
    return newProject;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return; // Prevent double-click
    
    if (!projectData.name || !projectData.address || !projectData.description || !projectData.brief) {
      showNotification('Missing Information', 'Please fill in all required fields', 'warning');
      return;
    }
    
    // Store data we need before closing
    const dataToSubmit = { ...projectData };
    const wasStage1 = projectData.stage === 1;
    
    // Close modal IMMEDIATELY
    setShowProjectModal(false);
    
    // Reset form
    setProjectData({
      name: '',
      address: '',
      lotSize: '',
      type: 'single',
      stage: 1,
      description: '',
      brief: '',
      storeys: 1,
      townhouseUnits: '3-5',
      majorRenovationStoreys: 1,
      supportingDocuments: [],
      previousStageCompleted: 'no',
      previousProjectRef: ''
    });
    
    // Show creating notification
    showNotification('Creating Project...', 'Please wait while we set up your project.', 'info');
    
    try {
      const newProject = await createProject(dataToSubmit);
      
      if (!newProject) {
        // createProject failed (showed its own error)
        return;
      }
      
      // FOR STAGE 1: Open payment modal for card capture after delay
      if (wasStage1) {
        showNotification(
          'Project Created!',
          'Please provide your card details to begin work.',
          'success'
        );
        
        // Open payment modal for card capture
        setTimeout(() => {
          setPaymentProject(newProject);
          setShowPaymentModal(true);
        }, 300);
      } else {
        showNotification(
          'Project Created Successfully!',
          'Please make the initial payment to begin work.',
          'success'
        );
      }
      
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      showNotification('Error', 'Failed to create project', 'error');
    }
  };

  // Get display cost for the pricing panel
  const getDisplayCost = () => {
    if (projectData.stage === 1) {
      const price = getStage1Price(projectData.type, projectData.townhouseUnits);
      return price ? `$${price}` : 'Quote Required';
    }
    
    if (projectData.stage === 2) {
      let baseStage2Cost = 0;
      
      if (projectData.type === 'single' || projectData.type === 'duplex') {
        if (projectData.storeys === 1) baseStage2Cost = 399;
        else if (projectData.storeys === 2) baseStage2Cost = 550;
        else if (projectData.storeys === 3) baseStage2Cost = 650;
      } else if (projectData.type === 'townhouse') {
        if (projectData.townhouseUnits === '3-5') baseStage2Cost = 799;
        else if (projectData.townhouseUnits === '6-9') baseStage2Cost = 1099;
      } else if (projectData.type === 'secondary' || projectData.type === 'minor-renovation') {
        baseStage2Cost = 299;
      } else if (projectData.type === 'major-renovation') {
        baseStage2Cost = 349;
      }
      
      if (hasCompletedStage1 === false) {
        const stage1Price = getStage1Price(projectData.type, projectData.townhouseUnits) || 199;
        return `$${baseStage2Cost + stage1Price} (includes $${stage1Price} Site Investigation)`;
      }
      
      return `$${baseStage2Cost}`;
    }
    
    return 'Quote Required';
  };

  const getAdditionalRevisionFee = () => {
    if (projectData.type === 'secondary' || projectData.type === 'minor-renovation') return '$99';
    if ((projectData.type === 'single' || projectData.type === 'duplex') && projectData.storeys === 1) return '$99';
    if (projectData.type === 'major-renovation') return '$99';
    return '$150';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Create New Project</h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block font-semibold mb-2">Project Name *</label>
              <input
                type="text"
                placeholder="e.g., Smith Residence Addition"
                value={projectData.name}
                onChange={(e) => setProjectData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Property Address *</label>
              <input
                type="text"
                placeholder="Full street address"
                value={projectData.address}
                onChange={(e) => setProjectData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Lot Size (sqm)</label>
              <input
                type="text"
                placeholder="e.g., 450"
                value={projectData.lotSize}
                onChange={(e) => setProjectData(prev => ({ ...prev, lotSize: e.target.value }))}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Development Type *</label>
              <select
                value={projectData.type}
                onChange={(e) => setProjectData(prev => ({ ...prev, type: e.target.value }))}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="single">Single Dwelling</option>
                <option value="duplex">Duplex</option>
                <option value="townhouse">Townhouse Development</option>
                <option value="secondary">Secondary Dwelling / Granny Flat</option>
                <option value="minor-renovation">Minor Renovation (under $100k)</option>
                <option value="major-renovation">Major Renovation ($100k+)</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold mb-2">Project Stage *</label>
              <select
                value={projectData.stage}
                onChange={(e) => setProjectData(prev => ({ ...prev, stage: parseInt(e.target.value) }))}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value={1}>Stage 1: Desktop Site Investigation & Feasibility</option>
                <option value={2}>Stage 2: Concept Design</option>
                {STAGE_3_4_ENABLED && (
                  <>
                    <option value={3}>Stage 3: Authority Approval Documentation</option>
                    <option value={4}>Stage 4: Construction Documentation</option>
                  </>
                )}
              </select>
            </div>

            {/* Stage 2 - Show Stage 1 completion status */}
            {projectData.stage === 2 && projectData.address && projectData.address.length > 5 && (
              <div className={`p-3 rounded-lg ${
                checkingStage1 ? 'bg-gray-100' :
                hasCompletedStage1 === true ? 'bg-green-50 border border-green-200' :
                hasCompletedStage1 === false ? 'bg-yellow-50 border border-yellow-200' :
                'bg-gray-100'
              }`}>
                {checkingStage1 ? (
                  <p className="text-sm text-gray-600">Checking Stage 1 completion status...</p>
                ) : hasCompletedStage1 === true ? (
                  <p className="text-sm text-green-700">✓ Stage 1 completed for this address. Standard Stage 2 pricing applies.</p>
                ) : hasCompletedStage1 === false ? (
                  <p className="text-sm text-yellow-700">ℹ️ No Stage 1 found for this address. Desktop Site Investigation will be included in your Stage 2 project.</p>
                ) : null}
              </div>
            )}

            {projectData.stage === 2 && (projectData.type === 'single' || projectData.type === 'duplex') && (
              <div>
                <label className="block font-semibold mb-2">Number of Storeys</label>
                <select
                  value={projectData.storeys}
                  onChange={(e) => setProjectData(prev => ({ ...prev, storeys: parseInt(e.target.value) }))}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value={1}>1 Storey</option>
                  <option value={2}>2 Storeys</option>
                  <option value={3}>3 Storeys</option>
                </select>
              </div>
            )}

            {projectData.type === 'townhouse' && (
              <div>
                <label className="block font-semibold mb-2">Number of Townhouses</label>
                <select
                  value={projectData.townhouseUnits}
                  onChange={(e) => setProjectData(prev => ({ ...prev, townhouseUnits: e.target.value }))}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="3-5">3-5 Townhouses</option>
                  <option value="6-9">6-9 Townhouses</option>
                </select>
              </div>
            )}

            {projectData.stage === 2 && projectData.type === 'major-renovation' && (
              <div>
                <label className="block font-semibold mb-2">Number of Storeys</label>
                <select
                  value={projectData.majorRenovationStoreys}
                  onChange={(e) => setProjectData(prev => ({ ...prev, majorRenovationStoreys: parseInt(e.target.value) }))}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value={1}>1 Storey</option>
                  <option value={2}>2 Storeys</option>
                </select>
              </div>
            )}

            <div>
              <label className="block font-semibold mb-2">Project Description *</label>
              <textarea
                rows={3}
                placeholder="Brief overview of your project requirements..."
                value={projectData.description}
                onChange={(e) => setProjectData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Detailed Requirements *</label>
              <textarea
                rows={5}
                placeholder="Please provide detailed requirements for your project..."
                value={projectData.brief}
                onChange={(e) => setProjectData(prev => ({ ...prev, brief: e.target.value }))}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Supporting Documents</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.dwg,.dxf"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="text-3xl mb-2">📎</div>
                  <p className="text-gray-600 text-center">
                    Click to upload files or drag and drop<br />
                    <span className="text-sm">PDF, JPG, PNG, DOC, DWG accepted</span>
                  </p>
                </label>
              </div>
              
              {projectData.supportingDocuments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {projectData.supportingDocuments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📄</span>
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 my-6">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-orange-800 mb-2">Pricing Information</h4>
              <p className="text-xs"><strong>Development Type:</strong> {typeNames[projectData.type]}</p>
              <p className="text-xs"><strong>Stage:</strong> Stage {projectData.stage}</p>
              <p className="text-xs"><strong>Cost:</strong> {
                projectData.stage >= 3 ? 'Custom Quote Required' : getDisplayCost()
              }</p>
              {projectData.stage === 2 && hasCompletedStage1 === false && (
                <p className="text-xs text-orange-700 mt-1">
                  <strong>Includes:</strong> Desktop Site Investigation + Concept Design
                </p>
              )}
              <p className="text-xs"><strong>Timeline:</strong> {
  projectData.stage === 1 ? (projectData.type === 'townhouse' ? (projectData.townhouseUnits === '6-9' ? '7-9 business days' : '5-7 business days') : '3-5 business days') :
  projectData.stage === 2 ? '3-9 business days for initial concept' :
  projectData.stage === 3 ? '4-8 weeks after quote approval' :
  projectData.stage === 4 ? 'TBA' :
  '5-7 business days'
}</p>
              {projectData.stage === 2 && (
                <p className="text-xs"><strong>Revisions:</strong> 1 revision included | Additional: {getAdditionalRevisionFee()} each</p>
              )}
              {projectData.stage >= 3 && (
  <>
    <div className="mt-2 text-xs text-orange-700">
      <strong>Quote Process:</strong> Assessment in approx. 3-5 business days, unless a site inspection is required
    </div>
    <p className="text-[10px] text-orange-700 italic mt-1">
      <strong>Note:</strong> If a site inspection is required, one of our human partners will be in touch to arrange site access.
    </p>
  </>
)}
              {projectData.stage === 2 && (
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <p className="text-[10px] text-orange-700 italic">
                    <strong>Important:</strong> Quoted fees are for standard site conditions and typical design complexity. 
                    planAIdesign reserves the right to adjust fees for sites with challenging constraints (such as steep slopes, 
                    irregular boundaries, or heritage overlays) or concept designs requiring exceptional complexity. 
                    Any fee adjustments will be communicated and agreed upon before work proceeds.
                    Complex sites and briefs may require additional consultation at planAIdesign's discretion.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Stage 1 Payment Terms - UPDATED FOR NEW MODEL */}
          {projectData.stage === 1 && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <h4 className="font-semibold text-green-800 mb-2">💳 Payment Terms - Stage 1</h4>
              <ul className="text-xs text-green-900 space-y-1">
                <li>• <strong>No payment required now</strong> — work begins immediately after card details are captured</li>
                <li>• Your card details are securely stored but will not be charged upfront</li>
                <li>• Draft watermarked report delivered within 3-5 business days</li>
                <li>• Invoice issued with draft report — payment due within 5 days</li>
                <li>• Final unwatermarked report released upon payment</li>
                <li>• <strong>Auto-charge:</strong> If invoice remains unpaid after 5 days, your card will be automatically charged and final report released</li>
              </ul>
              <p className="text-[10px] text-green-700 mt-3 italic">
                By proceeding, you authorise planAIdesign to charge your card for the quoted amount ({getDisplayCost()}) if payment is not received within 5 days of invoice.
              </p>
            </div>
          )}

          {/* Quality Assurance Footer */}
          <div className="bg-gray-50 p-3 rounded-lg mb-4 border-l-4 border-blue-500">
            <p className="text-xs text-gray-700">
              <strong>Quality Assurance:</strong> Your project will be prepared by qualified 
              professionals with extensive NSW council approval experience.
            </p>
          </div>
          
          <div className="flex gap-4">
            <button 
              onClick={() => setShowProjectModal(false)}
              className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleSubmit}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              {projectData.stage === 1 ? 'Create' : 'Create Project'}
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Table Modal */}
      <PricingTableModal 
        isOpen={showPricingTable} 
        onClose={() => setShowPricingTable(false)} 
      />
    </>
  );
};

export default ProjectModal;